import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useTranslation } from 'react-i18next';

import Modal from 'common/components/organisms/Modal';
import Loading from 'common/components/atoms/Loading';
import { GhostBtn } from 'common/components/molecules/ListToolbar';
import { Download, CheckCircle, AlertTriangle } from 'common/images/FaIcons';
import { machineCsvActions } from '../../machineCsv.actions';
import { EXPORT_SCOPE, KEY_COLUMN } from '../../machineCsv.constants';
import ColumnTable from './ColumnTable';
import ScopeChoice from './ScopeChoice';
import {
  defaultExportColumns, exportColumnsStorageKey, hasActiveView, readStoredColumns, storeColumns
} from './helpers';
import {
  Body, Muted, Section, SectionHead, ScopeGroup, Callout, Footer, FooterActions, PrimaryAction
} from './MachineCsv.styled';

function ExportModal({
  close, catalog, catalogError, exporting, viewCount, searchParams, storageKey, actions
}) {
  const { t } = useTranslation();
  const viewActive = hasActiveView(searchParams);
  const [selected, setSelected] = useState(null);
  const [scope, setScope] = useState(viewActive ? EXPORT_SCOPE.VIEW : EXPORT_SCOPE.ALL);

  // Fetched on every open: the site's machine count and the limits change.
  const loadCatalog = useCallback(() => actions.getCsvCatalog(), [actions]);
  useEffect(() => {
    loadCatalog();
  }, []);

  useEffect(() => {
    if (catalog && selected === null) {
      setSelected(readStoredColumns(catalog.columns, storageKey) || defaultExportColumns(catalog.columns));
    }
  }, [catalog]);

  const toggleColumn = useCallback((key) => {
    setSelected(current => (current.includes(key)
      ? current.filter(item => item !== key)
      : [...current, key]));
  }, []);

  const setAll = useCallback((keys, on) => {
    setSelected(current => (on
      ? [...current, ...keys.filter(key => !current.includes(key))]
      : current.filter(key => !keys.includes(key))));
  }, []);

  const handleExport = useCallback(() => {
    storeColumns(selected, storageKey);
    actions.exportMachinesCsv(selected, scope).then((saved) => {
      if (saved) close();
    });
  }, [selected, scope, actions, close, storageKey]);

  if (!catalog && catalogError) {
    return (
      <Modal title={t('assets.csv.export.title', { defaultValue: 'Export assets' })} width="38rem" padding="0" close={close}>
        <Body>
          <Callout tone="crit" role="alert">
            <AlertTriangle />
            {t('assets.csv.export.catalogFailed', { defaultValue: 'The list of columns could not be loaded.' })}
          </Callout>
          <Footer>
            <span />
            <FooterActions>
              <GhostBtn type="button" onClick={close}>{t('common.cancel', { defaultValue: 'Cancel' })}</GhostBtn>
              <PrimaryAction type="button" onClick={loadCatalog}>
                {t('common.retry', { defaultValue: 'Retry' })}
              </PrimaryAction>
            </FooterActions>
          </Footer>
        </Body>
      </Modal>
    );
  }

  if (!catalog || selected === null) {
    return (
      <Modal title={t('assets.csv.export.title', { defaultValue: 'Export assets' })} width="38rem" padding="0" close={close}>
        <Loading />
      </Modal>
    );
  }

  const total = catalog.columns.length;
  const picked = selected.filter(key => key !== KEY_COLUMN).length + 1;
  const assetsCount = count => t('assets.csv.export.assetsCount', { count, defaultValue: '{{count}} assets' });
  const exportedCount = scope === EXPORT_SCOPE.VIEW ? viewCount : catalog.machine_count;
  const overImportLimit = exportedCount !== null && exportedCount > catalog.max_rows;

  return (
    <Modal title={t('assets.csv.export.title', { defaultValue: 'Export assets' })} width="38rem" padding="0" close={close}>
      <Body>
        <Section>
          <SectionHead id="csv-export-scope">
            {t('assets.csv.export.whichAssets', { defaultValue: 'Which assets' })}
          </SectionHead>
          <ScopeGroup role="radiogroup" aria-labelledby="csv-export-scope">
            {viewActive && (
              <ScopeChoice
                scope={EXPORT_SCOPE.VIEW}
                selected={scope === EXPORT_SCOPE.VIEW}
                label={t('assets.csv.export.currentView', { defaultValue: 'Current view' })}
                detail={t('assets.csv.export.currentViewDetail', { defaultValue: 'the filters and search on this page' })}
                count={viewCount === null ? null : assetsCount(viewCount)}
                onSelect={setScope}
              />
            )}
            <ScopeChoice
              scope={EXPORT_SCOPE.ALL}
              selected={scope === EXPORT_SCOPE.ALL}
              label={t('assets.csv.export.allAssets', { defaultValue: 'All assets in this site' })}
              count={assetsCount(catalog.machine_count)}
              onSelect={setScope}
            />
          </ScopeGroup>
          {overImportLimit && (
            <Callout tone="warn" role="status">
              <AlertTriangle />
              {t('assets.csv.export.overImportLimit', {
                count: exportedCount,
                max: catalog.max_rows,
                defaultValue: 'This file will hold {{count}} assets. An import accepts {{max}} rows per file, so split it into smaller files before importing it back.'
              })}
            </Callout>
          )}
        </Section>
        <Section>
          <SectionHead>{t('assets.csv.export.columns', { defaultValue: 'Columns' })}</SectionHead>
          <ColumnTable
            columns={catalog.columns}
            selected={selected}
            onToggle={toggleColumn}
            onSetAll={setAll}
          />
          <Muted>
            {t('assets.csv.export.keyHint', { defaultValue: 'Needed to import your changes back. Do not edit this column.' })}
          </Muted>
        </Section>
        <Callout>
          <CheckCircle />
          {t('assets.csv.export.renameHelp', {
            defaultValue: 'To rename machines in bulk: export with Name, edit the names in Excel or Google Sheets, save as CSV (UTF-8), then use Import. You will see every change before anything is saved.'
          })}
        </Callout>
        <Footer>
          <Muted>
            {t('assets.csv.export.columnsPicked', { picked, total, defaultValue: '{{picked}} of {{total}} columns' })}
          </Muted>
          <FooterActions>
            <GhostBtn type="button" onClick={close}>{t('common.cancel', { defaultValue: 'Cancel' })}</GhostBtn>
            <PrimaryAction type="button" onClick={handleExport} disabled={exporting || picked < 2}>
              <Download />
              {exporting
                ? t('assets.csv.export.exporting', { defaultValue: 'Exporting...' })
                : t('assets.csv.export.submit', { defaultValue: 'Export CSV' })}
            </PrimaryAction>
          </FooterActions>
        </Footer>
      </Body>
    </Modal>
  );
}

ExportModal.propTypes = {
  close: PropTypes.func.isRequired,
  catalog: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.object),
    machine_count: PropTypes.number,
    max_rows: PropTypes.number
  }),
  catalogError: PropTypes.object,
  exporting: PropTypes.bool.isRequired,
  viewCount: PropTypes.number,
  searchParams: PropTypes.object,
  storageKey: PropTypes.string,
  actions: PropTypes.shape({
    getCsvCatalog: PropTypes.func.isRequired,
    exportMachinesCsv: PropTypes.func.isRequired
  }).isRequired
};

ExportModal.defaultProps = {
  catalog: null,
  catalogError: null,
  viewCount: null,
  searchParams: null,
  storageKey: exportColumnsStorageKey()
};

const mapStateToProps = (state) => {
  const { items, searchParams, csv } = state.machines;
  return {
    catalog: csv.catalog.data,
    catalogError: csv.catalog.error,
    exporting: csv.exporting,
    storageKey: exportColumnsStorageKey(
      state.user && state.user.user && state.user.user.id,
      state.currentAccount && state.currentAccount.id
    ),
    viewCount: items && typeof items.total_count === 'number' ? items.total_count : null,
    searchParams
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(machineCsvActions, dispatch)
});

export { ExportModal };
export default connect(mapStateToProps, mapDispatchToProps)(ExportModal);
